import { createSlice } from '@reduxjs/toolkit';

const reducerName = 'quickAppReviewData';
const initialState = {
  sort: {
    id: 'updated_at',
    label: 'Last activity (New to old)',
    element: 'updated_at',
    order: 'desc',
  },
  filters: {
    credited_to_user_ids: [],
    skill_ids: [],
    tag_ids: [],
    current_company_ids: [],
    institute_ids: [],
  },
  isFirstCallDone: false,
  page: {
    total: 0,
    offset: 0,
    limit: 10,
    scroll_token: null,
  },
  rejectionConfig: null,
  applicationsData: [],
  currentApplication: {},
  movedApplications: [],
  rejectedApplications: [],
  currentAppCount: 0,
  reviewStartTime: null,
  rememberRejectChoice: true,
  rememberMoveToNextStepChoice: false,
  moveToNextStepConfig: null,
  currentAppNotes: { data: [], meta: {} },
};

const { reducer, actions: quickAppReviewActions } = createSlice({
  name: reducerName,
  initialState,

  reducers: {
    setApplicationsData: (state, { payload }) => {
      const newApplicationsWithDefValues = payload?.applications?.map(
        (item) => ({
          ...item,
          isDetailsLoaded: false,
          isDetailsLoading: false,
          detailsData: {},
        })
      );

      // Get existing application IDs to prevent duplicates
      const existingIds = new Set(
        state?.applicationsData?.map((app) => app?.id) || []
      );

      // Filter out duplicates based on application ID
      const uniqueNewApplications =
        newApplicationsWithDefValues?.filter(
          (app) => app?.id && !existingIds.has(app.id)
        ) || [];

      state.applicationsData = [
        ...state?.applicationsData,
        ...uniqueNewApplications,
      ];
      // Only set currentApplication and currentAppCount on the first call (initial load)
      // When appending new pages, keep the current application unchanged
      if (!state.isFirstCallDone) {
        state.currentApplication = newApplicationsWithDefValues?.[0] || {};
        state.currentAppCount = 1;
        state.isFirstCallDone = true;
      }
      state.page = payload.pagination;
    },

    handleNextClick: (state) => {
      const newAppIndex = state.currentAppCount;
      // Only prevent navigation if we're clearly beyond bounds (more than 1 index past)
      // Allow navigation if we're exactly at the end, as next page might be loading
      if (newAppIndex > state?.applicationsData?.length) {
        return; // Don't update if we're clearly beyond the end
      }
      // If we're at the end but there might be more data, still allow navigation
      // The component will handle showing loading state if needed
      const nextApplication = state?.applicationsData?.at(newAppIndex);
      if (nextApplication) {
        state.currentApplication = nextApplication;
        state.currentAppCount = newAppIndex + 1;
        state.currentAppNotes = { data: [], meta: {} };
      }
      // If nextApplication is undefined, we're at the end - don't update but don't error
    },

    setReviewStartTime: (state, { payload }) => {
      const { time } = payload;
      state.reviewStartTime = time;
    },

    handlePrevClick: (state) => {
      const newAppIndex = state.currentAppCount - 2;
      state.currentApplication = state?.applicationsData?.at(newAppIndex);
      state.currentAppCount = newAppIndex + 1;
      state.currentAppNotes = { data: [], meta: {} };
    },

    setPaginationData: (state, { payload }) => {
      state.page = payload.page;
    },

    resetToInitialData: (state, { payload }) => {
      state.applicationsData = initialState.applicationsData;
      state.isFirstCallDone = initialState.isFirstCallDone;
      state.page = initialState.page;
      state.movedApplications = initialState.movedApplications;
      state.rejectedApplications = initialState.rejectedApplications;
      state.rejectionConfig = initialState.rejectionConfig;
      state.rememberRejectChoice = initialState.rememberRejectChoice;
      state.rememberMoveToNextStepChoice =
        initialState.rememberMoveToNextStepChoice;
      state.moveToNextStepConfig = initialState.moveToNextStepConfig;
    },

    setRejectionConfig: (state, { payload = {} }) => {
      let processedPayload = { ...payload };
      if (processedPayload?.selectedTemplate) {
        processedPayload.selectedTemplate = {
          ...processedPayload.selectedTemplate,
          body: processedPayload.body,
          subject: processedPayload.subject,
        };
      }

      state.rejectionConfig = processedPayload;
    },

    toggleRememberChoice: (state) => {
      state.rememberRejectChoice = !state.rememberRejectChoice;
    },

    setMoveToNextStepConfig: (state, { payload = null }) => {
      state.moveToNextStepConfig = payload;
    },

    toggleRememberMoveToNextStepChoice: (state) => {
      state.rememberMoveToNextStepChoice = !state.rememberMoveToNextStepChoice;
    },

    setAppDetailsLoading: (state, { payload }) => {
      const currAppId = payload.appId;
      const currApp = state.applicationsData?.find(
        (app) => app?.id === currAppId
      );
      const newCurrApp = { ...currApp, isDetailsLoading: true };
      state.currentApplication = newCurrApp;
      state.applicationsData = state.applicationsData?.reduce((acc, curr) => {
        if (curr?.id === currAppId) {
          return [...acc, newCurrApp];
        } else return [...acc, curr];
      }, []);
    },

    setAppDetailsData: (state, { payload }) => {
      const { appId: currAppId, appDetailsData } = payload;
      const currApp = state.applicationsData?.find(
        (app) => app?.id === currAppId
      );
      const newCurrApp = {
        ...currApp,
        isDetailsLoading: false,
        isDetailsLoaded: true,
        detailsData: appDetailsData?.data || {},
      };
      state.currentApplication = newCurrApp;
      state.applicationsData = state.applicationsData?.reduce((acc, curr) => {
        if (curr?.id === currAppId) {
          return [...acc, newCurrApp];
        } else return [...acc, curr];
      }, []);
    },

    setTagsUpdate: (state, { payload }) => {
      const { appId: currAppId, tagsData } = payload;
      const currApp = state.applicationsData?.find(
        (app) => app?.id === currAppId
      );
      const newCurrApp = {
        ...currApp,
        detailsData: {
          ...currApp?.detailsData,
          contact: { ...currApp?.detailsData?.contact, tags: tagsData },
        },
      };
      state.currentApplication = newCurrApp;
      state.applicationsData = state.applicationsData?.reduce((acc, curr) => {
        if (curr?.id === currAppId) {
          return [...acc, newCurrApp];
        } else return [...acc, curr];
      }, []);
    },

    setUpdateAppData: (state, { payload }) => {
      const { appId: currAppId, data, type } = payload;
      const currApp = state.applicationsData?.find(
        (app) => app?.id === currAppId
      );
      const {
        job_stage_id,
        last_stage_changed_at,
        status,
        updated_at,
        ats_rejection_reason,
      } = data;
      const updatedValues = {
        job_stage_id,
        last_stage_changed_at,
        status,
        updated_at,
        ats_rejection_reason,
      };
      const newCurrApp = {
        ...currApp,
        ...updatedValues,
        detailsData: { ...currApp?.detailsData, ...updatedValues },
      };
      if (type === 'move') {
        state.movedApplications = [...state.movedApplications, currAppId];
      }
      if (type === 'reject') {
        state.rejectedApplications = [...state.rejectedApplications, currAppId];
      }
      if (type === 'moveBackToApp') {
        const newMovedApps = state.movedApplications?.filter(
          (i) => i !== currAppId
        );
        state.movedApplications = [...newMovedApps];
      }
      if (type === 'unreject') {
        const newRejectedApps = state.rejectedApplications?.filter(
          (i) => i !== currAppId
        );
        state.rejectedApplications = [...newRejectedApps];
      }
      state.currentApplication = newCurrApp;
      state.applicationsData = state.applicationsData?.reduce((acc, curr) => {
        if (curr?.id === currAppId) {
          return [...acc, newCurrApp];
        } else return [...acc, curr];
      }, []);
    },

    addApplicationNotes: (state, { payload }) => {
      const { notes = [], meta = null, isInitial = false } = payload;
      if (isInitial) {
        state.currentAppNotes.data = [...notes];
        state.currentAppNotes.meta = meta;
      } else {
        state.currentAppNotes.data = [
          ...state?.currentAppNotes?.data,
          ...notes,
        ];
        state.currentAppNotes.meta = meta;
      }
    },

    addApplicationNote: (state, { payload }) => {
      const { newNote } = payload;
      state.currentAppNotes.data =
        state?.currentAppNotes?.data?.length > 0
          ? [newNote, ...state?.currentAppNotes?.data]
          : [newNote];
      state.currentAppNotes.meta = {
        ...state.currentAppNotes.meta,
        count: (state?.currentAppNotes?.meta?.count || 0) + 1,
      };
    },

    updateApplicationNote: (state, { payload }) => {
      const { note, newNote } = payload;
      state.currentAppNotes.data = state?.currentAppNotes?.data?.map?.(
        (item) => {
          if (item?.id === note?.id) return newNote;
          else return item;
        }
      );
    },

    setLoadingState: (state, { payload }) => {
      const { key, isLoading } = payload || {};
      if (state[key]) {
        state[key].isLoading = isLoading;
      }
    },

    setApiData: (state, { payload }) => {
      const { key, data, status, error, isLoading } = payload || {};
      if (state[key]) {
        state[key].data = data;
        state[key].isLoading = isLoading;
        state[key].status = status;
        if (error) {
          state[key].error = error;
        }
      }
    },

    setCurrentApplication: (state, { payload }) => {
      const { application } = payload;
      if (application) {
        state.currentApplication = application;
      }
    },

    setCurrentAppCount: (state, { payload }) => {
      const { count } = payload;
      if (count !== undefined) {
        state.currentAppCount = count;
      }
    },
  },
});

export { quickAppReviewActions };
export default reducer;
